<script setup lang="ts">
import { computed } from "vue";
import { DotLottieVue } from "@lottiefiles/dotlottie-vue";
import { Button } from "@/components/ui/button";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

const props = withDefaults(
  defineProps<{
    companyName?: string | null;
    needsReviewCount?: number;
    completedTasksCount?: number;
    totalTasksCount?: number;
    overallProgress?: number;
    selectedPeriod?: string;
  }>(),
  {
    companyName: "",
    needsReviewCount: 0,
    completedTasksCount: 0,
    totalTasksCount: 0,
    overallProgress: undefined,
    selectedPeriod: "this_month",
  }
);

const emit = defineEmits<{
  (event: "update:period", period: string): void;
  (event: "new-request"): void;
  (event: "contact-support"): void;
  (event: "review-click"): void;
}>();

const clientName = computed(() => {
  return props.companyName || "Valued Client";
});

const progressPercent = computed(() => {
  if (props.overallProgress !== undefined && props.overallProgress !== null) {
    return Math.round(props.overallProgress);
  }
  if (props.totalTasksCount > 0) {
    return Math.round((props.completedTasksCount / props.totalTasksCount) * 100);
  }
  return 0;
});

const periodLabel = computed(() => {
  switch (props.selectedPeriod) {
    case "last_month":
      return "last month";
    case "last_90_days":
      return "in the last 90 days";
    case "all_time":
      return "all-time";
    case "this_month":
    default:
      return "this month";
  }
});
</script>

<template>
  <div
    class="relative flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-6 rounded-3xl bg-gradient-to-r from-[#edf4ff] via-white to-white border border-slate-100 p-6 md:p-8 text-left shadow-sm"
  >
    <!-- Left + Middle: Lottie Graphic & Welcome Content -->
    <div class="flex flex-col sm:flex-row items-center sm:items-start xl:items-center gap-6 flex-1 min-w-0">
      <div class="shrink-0 flex items-center justify-center">
        <DotLottieVue
          style="height: 120px; width: 150px; transform: rotate(-10deg);"
          autoplay
          loop
          src="https://lottie.host/22d0ec44-e355-4465-8e4a-297dc4928739/gKTrWTYDRE.json"
        />
      </div>

      <!-- Welcome text and actions -->
      <div class="space-y-3 flex-1 min-w-0 text-center sm:text-left">
        <div>
          <h2 class="text-2xl md:text-3xl font-black text-slate-800 tracking-tight">
            Welcome back, {{ clientName }}!
          </h2>
          <p class="text-sm md:text-base text-slate-500 font-semibold leading-relaxed mt-1">
            Your marketing is moving forward. You have
            <button
              type="button"
              class="text-[#1f7aff] font-black underline decoration-2 decoration-[#1f7aff]/30 hover:text-[#185adb] transition-colors"
              @click="$emit('review-click')"
            >
              {{ needsReviewCount }} {{ needsReviewCount === 1 ? 'item' : 'items' }}
            </button>
            that need your review and
            <span class="text-[#1f7aff] font-black">{{ completedTasksCount }} deliverables</span>
            completed {{ periodLabel }}.
          </p>
        </div>

        <!-- Quick Client Action Buttons -->
        <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 pt-1">
          <Button
            size="sm"
            class="bg-[#1f7aff] hover:bg-[#185adb] text-white font-bold rounded-xl h-9 px-4 text-xs shadow-xs flex items-center gap-1.5 transition-all"
            @click="$emit('new-request')"
          >
            <DMSuiteIcon :icon="DASHBOARD_ICONS.plus" :size="15" />
            New Request
          </Button>

          <Button
            size="sm"
            variant="outline"
            class="border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-xl h-9 px-4 text-xs shadow-xs flex items-center gap-1.5 transition-all"
            @click="$emit('contact-support')"
          >
            <DMSuiteIcon :icon="DASHBOARD_ICONS.socialMedia" :size="15" custom-class="text-[#1f7aff]" />
            Contact Support
          </Button>

          <!-- Reporting-Period Selector -->
          <div class="inline-flex items-center gap-1.5 pl-1 sm:pl-2">
            <span class="text-xs font-bold text-slate-400 flex items-center gap-1">
              <DMSuiteIcon :icon="DASHBOARD_ICONS.calendar" :size="15" custom-class="text-slate-400" />
              Period:
            </span>
            <select
              :value="selectedPeriod"
              class="h-9 rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1f7aff]/20 shadow-xs cursor-pointer"
              @change="$emit('update:period', ($event.target as HTMLSelectElement).value)"
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_90_days">Last 90 Days</option>
              <option value="all_time">All Time</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Right: Overall Progress indicator -->
    <div class="w-full xl:w-72 border-t xl:border-t-0 xl:border-l border-slate-100 pt-5 xl:pt-0 xl:pl-6 space-y-2 shrink-0">
      <div class="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
        <span>Overall Completion</span>
        <span class="text-[11px] font-semibold text-slate-400 lowercase">all-time scope</span>
      </div>

      <div class="flex items-baseline gap-2">
        <span class="text-3xl font-black text-slate-800">{{ progressPercent }}%</span>
        <span
          v-if="totalTasksCount > 0"
          class="text-xs font-semibold text-slate-500"
        >
          ({{ completedTasksCount }} / {{ totalTasksCount }} tasks)
        </span>
      </div>

      <div class="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          class="h-full bg-blue-600 rounded-full transition-all duration-500"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>

      <p class="text-[11px] font-semibold text-slate-400 pt-0.5">
        {{ totalTasksCount > 0 ? `${completedTasksCount} of ${totalTasksCount} total tasks completed` : 'No active tasks scheduled' }}
      </p>
    </div>
  </div>
</template>
